import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { Typography } from '@euler/app/components/typography';
import { MemberCell } from '@euler/app/flows/common/components/MemberCell';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useMemberList } from '@euler/functions/useMemberList';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { OrgMember } from '@euler/model/entity';
import { BottomSheetModal, BottomSheetSectionList } from '@gorhom/bottom-sheet';
import {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, ScrollView, SectionList } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const Header = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Center
      css={`
        padding: ${rpx(10)}px 0;
        border-bottom-width: 1px;
        border-bottom-color: #f1f1f1;
        height: ${rpx(44)}px;
        background-color: #fff;
      `}
    >
      <Typography.Label size={rpx(16)} color="#333" medium>
        选择质检员
      </Typography.Label>
    </Center>
  );
});

const Content = memo(
  ({
    onSelect,
  }: {
    selected: number;
    onSelect?: (member: OrgMember) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const members = useMemberList()!;
    const sections = useMemo(
      () => [
        {
          title: '选择质检员',
          data: members,
        },
      ],
      [members],
    );
    const renderHeader = useCallback(() => <Header />, []);
    const ListComponent =
      Platform.OS === 'web' ? SectionList : BottomSheetSectionList;
    return (
      <ListComponent
        sections={sections}
        contentContainerStyle={{
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
          paddingBottom: insets.bottom + rpx(15),
        }}
        keyExtractor={x => String(x.id)}
        renderSectionHeader={renderHeader}
        renderItem={({ item, index }) => (
          <MemberCell
            member={item}
            first={index === 0}
            last={index === members.length - 1}
            onPress={onSelect}
            isRoleLabelVisible
          />
        )}
      />
    );
  },
);

const Fallback = () => {
  const { navBarHeight } = useSystemMetrics();
  const { rpx } = useResponsiveContext();
  return (
    <ScrollView
      css={`
        flex: 1;
      `}
      contentContainerStyle={[
        {
          flexGrow: 1,
          paddingTop: navBarHeight + rpx(15),
          justifyContent: 'flex-start',
          alignItems: 'center',
        },
      ]}
    >
      <DefaultLoader />
    </ScrollView>
  );
};

export const DeliveryCheckUserBottomSheetModal = memo(
  ({
    visible,
    userId,
    onSelect,
    onDismiss,
  }: {
    visible?: boolean;
    userId: number;
    editable?: boolean;
    onSelect?: (technician: OrgMember) => void;
    onDismiss?: () => void;
  }) => {
    const [show, setShow] = useState(visible);
    const { navBarHeight } = useSystemMetrics();

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: visible,
        topMargin: navBarHeight,
        minSnapPoint: '50%',
        mediumSnapPoint: '75%',
        index: 1,
        enableDismissOnClose: true,
        enablePanDownToClose: true,
        dismissWhenPressBackdrop: true,
        onDismiss,
      });

    useEffect(() => {
      if (visible) setShow(true);
    }, [visible]);

    return (
      <BottomSheetModal {...bottomSheetProps} ref={bottomSheetModalRef}>
        {show && (
          <Suspense fallback={<Fallback />}>
            <Content selected={userId} onSelect={onSelect} />
          </Suspense>
        )}
      </BottomSheetModal>
    );
  },
);
